import React, { useEffect, useState, createContext, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download } from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/format";
import ScheduleIIINotes from "@/pages/ScheduleIIINotes";
import Registers from "@/pages/Registers";
import BOMRevAuditor from "@/pages/reports/BOMRevAuditor";
import WorkflowRegisters from "@/pages/reports/WorkflowRegisters";
import POViolations from "@/pages/reports/POViolations";
import ReservedStockLedger from "@/pages/reports/ReservedStockLedger";
import AssemblyVariance from "@/pages/reports/AssemblyVariance";
import ValuationRecon from "@/pages/reports/ValuationRecon";
import SlowMovingStock from "@/pages/reports/SlowMovingStock";
import StockAgeing from "@/pages/reports/StockAgeing";
import ExpiryWatchdog from "@/pages/reports/ExpiryWatchdog";
import BatchRecall from "@/pages/reports/BatchRecall";
import { useDrillDown, DrillProvider, EntryChip, DrillCell, DrillCtx } from "@/components/reports/DrillDownDrawer";
import SendReportButton from "@/components/common/SendReportButton";
import ConstitutionAwareFS, { NotesConstitutionAware } from "@/pages/reports/ConstitutionAwareFS";

const today = () => new Date().toISOString().slice(0, 10);
const fyStart = () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return `${y}-04-01`; };
const FilterCtx = createContext({ from: fyStart(), to: today() });

function FilterBar() {
  const { from, to, setFrom, setTo } = useContext(FilterCtx);
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = today.getMonth();   // 0-based
  const startOfMonth = new Date(yyyy, mm, 1).toISOString().slice(0, 10);
  const endOfMonth = new Date(yyyy, mm + 1, 0).toISOString().slice(0, 10);
  const lastMonthStart = new Date(yyyy, mm - 1, 1).toISOString().slice(0, 10);
  const lastMonthEnd = new Date(yyyy, mm, 0).toISOString().slice(0, 10);
  const thisFyStartY = mm >= 3 ? yyyy : yyyy - 1;
  const thisFyStart = `${thisFyStartY}-04-01`;
  const thisFyEnd = today.toISOString().slice(0, 10);
  const lastFyStart = `${thisFyStartY - 1}-04-01`;
  const lastFyEnd = `${thisFyStartY}-03-31`;
  const presets = [
    { k: "tm", label: "This Month", f: startOfMonth, t: endOfMonth },
    { k: "lm", label: "Last Month", f: lastMonthStart, t: lastMonthEnd },
    { k: "tfy", label: "This FY", f: thisFyStart, t: thisFyEnd },
    { k: "lfy", label: "Last FY", f: lastFyStart, t: lastFyEnd },
  ];
  const activeKey = presets.find(p => p.f === from && p.t === to)?.k || "custom";
  const [showCustom, setShowCustom] = useState(activeKey === "custom");
  return (
    <div className="card-sharp p-3 mb-4 flex gap-2 items-center flex-wrap" data-testid="reports-filter-bar">
      <span className="text-[10px] font-mono uppercase text-muted-foreground mr-2">Period:</span>
      {presets.map(p => (
        <Button key={p.k} size="sm" variant={activeKey === p.k ? "default" : "outline"}
          onClick={() => { setFrom(p.f); setTo(p.t); setShowCustom(false); }}
          data-testid={`preset-${p.k}`}
          className="rounded-sm h-8 text-xs">{p.label}</Button>
      ))}
      <Button size="sm" variant={activeKey === "custom" ? "default" : "outline"}
        onClick={() => setShowCustom(v => !v)} data-testid="preset-custom" className="rounded-sm h-8 text-xs">Custom</Button>
      {(showCustom || activeKey === "custom") && (
        <>
          <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 w-40 ml-2" data-testid="reports-from" />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 w-40" data-testid="reports-to" />
        </>
      )}
      <span className="text-[11px] text-muted-foreground ml-auto font-mono">{from} → {to}</span>
    </div>
  );
}

const downloadXlsx = (path, filename) => downloadBlob(path, filename);

const _S3_LABELS_PL = {
  revenue_from_operations: "Revenue from operations",
  other_income: "Other income",
  cost_of_materials: "Cost of materials consumed",
  changes_in_inventories: "Changes in inventories of FG, WIP & stock-in-trade",
  employee_benefit_expense: "Employee benefit expense",
  finance_costs: "Finance costs",
  depreciation_amortisation: "Depreciation & amortisation expense",
  other_expenses: "Other expenses",
};

const _S3_LABELS_BS = {
  share_capital: "Share capital",
  reserves_and_surplus: "Reserves & Surplus",
  long_term_borrowings: "Long-term borrowings",
  deferred_tax_liabilities_net: "Deferred tax liabilities (net)",
  long_term_provisions: "Long-term provisions",
  short_term_borrowings: "Short-term borrowings",
  trade_payables: "Trade payables",
  other_current_liabilities: "Other current liabilities",
  short_term_provisions: "Short-term provisions",
  fixed_assets_tangible: "Tangible fixed assets",
  fixed_assets_intangible: "Intangible assets",
  capital_wip: "Capital work-in-progress",
  non_current_investments: "Non-current investments",
  long_term_loans_and_advances: "Long-term loans & advances",
  current_investments: "Current investments",
  inventories: "Inventories",
  trade_receivables: "Trade receivables",
  cash_and_cash_equivalents: "Cash & cash equivalents",
  short_term_loans_and_advances: "Short-term loans & advances",
  other_current_assets: "Other current assets",
};

function PLScheduleIII() {
  const { from, to } = useContext(FilterCtx);
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/pnl-schedule3", { params: { from_date: from, to_date: to } }).then((r) => setD(r.data)); }, [from, to]);
  if (!d) return <div className="text-muted-foreground p-4">Loading…</div>;
  const openBucket = (k, val) => {
    const rows = (d.line_details && d.line_details[k]) || [];
    drill.openSubcategory(`${_S3_LABELS_PL[k] || k} · ${fmtINR(val)}`, rows);
  };
  const bucketRow = (k, val, indent = 0) => {
    const rows = (d.line_details && d.line_details[k]) || [];
    const has = rows.length > 0;
    return (
      <tr
        key={k}
        className={has ? "cursor-pointer group hover:bg-muted/40" : ""}
        onClick={() => has && openBucket(k, val)}
        data-testid={`pls3-row-${k}`}
      >
        <td className={indent ? "pl-8 text-sm text-muted-foreground" : "text-sm font-medium"}>
          <span className={has ? "group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2" : ""}>{_S3_LABELS_PL[k] || k}</span>
          {has && <EntryChip count={rows.reduce((s, r) => s + (r.entry_count || 0), 0)} />}
        </td>
        <td className="text-right tabular-nums font-mono">{fmtINR(val)}</td>
      </tr>
    );
  };
  return (
    <div className="card-sharp p-5" data-testid="pnl-schedule3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Statement of Profit & Loss · Schedule III · Companies Act 2013</div>
      <div className="text-xs text-muted-foreground mb-3">Period: {d.period.from} to {d.period.to} · <span className="italic">Click any bucket to see contributing accounts.</span></div>
      <table className="w-full dense">
        <thead><tr><th>Particulars</th><th className="text-right">₹</th></tr></thead>
        <tbody>
          <tr className="border-t"><td colSpan="2" className="pt-2 text-[10px] font-mono uppercase text-muted-foreground">I. Revenue</td></tr>
          {bucketRow("revenue_from_operations", d.revenue.revenue_from_operations)}
          {bucketRow("other_income", d.revenue.other_income)}
          <tr className="font-medium border-t"><td>Total Revenue (I)</td><td className="text-right tabular-nums font-mono">{fmtINR(d.revenue.total_revenue)}</td></tr>
          <tr className="border-t"><td colSpan="2" className="pt-2 text-[10px] font-mono uppercase text-muted-foreground">II. Expenses</td></tr>
          {["cost_of_materials","changes_in_inventories","employee_benefit_expense","finance_costs","depreciation_amortisation","other_expenses"].map((k) => bucketRow(k, d.expenses[k]))}
          <tr className="font-medium border-t"><td>Total Expenses (II)</td><td className="text-right tabular-nums font-mono">{fmtINR(d.total_expenses)}</td></tr>
          <tr className="border-t"><td className="font-medium">Profit Before Tax (I - II)</td><td className="text-right tabular-nums font-mono font-medium">{fmtINR(d.profit_before_tax)}</td></tr>
          <tr><td className="text-sm text-muted-foreground">Tax expense</td><td className="text-right tabular-nums font-mono">{fmtINR(d.tax_expense)}</td></tr>
          <tr className="border-t bg-accent/5 font-semibold"><td className="py-2">Profit / (Loss) for the Period</td><td className="text-right tabular-nums font-mono py-2">{fmtINR(d.profit_after_tax)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function BSScheduleIII() {
  const { to } = useContext(FilterCtx);
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/balance-sheet-schedule3", { params: { as_of: to } }).then((r) => setD(r.data)); }, [to]);
  if (!d) return <div className="text-muted-foreground p-4">Loading…</div>;
  // Map UI keys (from _S3_LABELS_BS) to backend line_details keys (BUCKETS_BS).
  const S3_TO_BUCKET_KEY = {
    share_capital: "equity_share_capital",
    reserves_and_surplus: "reserves_surplus",
    long_term_borrowings: "long_term_borrowings",
    deferred_tax_liabilities_net: "deferred_tax_liabilities",
    long_term_provisions: "long_term_provisions",
    short_term_borrowings: "short_term_borrowings",
    trade_payables: "trade_payables",
    other_current_liabilities: "other_current_liabilities",
    short_term_provisions: "short_term_provisions",
    fixed_assets_tangible: "fixed_assets_tangible",
    fixed_assets_intangible: "fixed_assets_intangible",
    capital_wip: "capital_wip",
    non_current_investments: "noncurrent_investments",
    long_term_loans_and_advances: "long_term_loans_advances",
    current_investments: "current_investments",
    inventories: "inventories",
    trade_receivables: "trade_receivables",
    cash_and_cash_equivalents: "cash_bank",
    short_term_loans_and_advances: "short_term_loans_advances",
    other_current_assets: "other_current_assets",
  };
  const openBucket = (uiKey, val) => {
    const backKey = S3_TO_BUCKET_KEY[uiKey];
    const rows = (backKey && d.line_details && d.line_details[backKey]) || [];
    drill.openSubcategory(`${_S3_LABELS_BS[uiKey] || uiKey} · ${fmtINR(val)}`, rows);
  };
  const sect = (title, keys, obj) => (
    <>
      <tr className="border-t"><td colSpan="2" className="pt-2 text-[10px] font-mono uppercase text-muted-foreground">{title}</td></tr>
      {keys.filter((k) => k !== "total").map((k) => {
        const backKey = S3_TO_BUCKET_KEY[k];
        const rows = (backKey && d.line_details && d.line_details[backKey]) || [];
        const has = rows.length > 0;
        return (
          <tr
            key={k}
            className={has ? "cursor-pointer group hover:bg-muted/40" : ""}
            onClick={() => has && openBucket(k, obj[k])}
            data-testid={`bss3-row-${k}`}
          >
            <td className="pl-8 text-sm text-muted-foreground">
              <span className={has ? "group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2" : ""}>{_S3_LABELS_BS[k] || k}</span>
              {has && <EntryChip count={rows.reduce((s, r) => s + (r.entry_count || 0), 0)} />}
            </td>
            <td className="text-right tabular-nums font-mono">{fmtINR(obj[k])}</td>
          </tr>
        );
      })}
      <tr className="font-medium"><td>Sub-total</td><td className="text-right tabular-nums font-mono">{fmtINR(obj.total)}</td></tr>
    </>
  );
  return (
    <div className="card-sharp p-5" data-testid="bs-schedule3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Balance Sheet · Schedule III · Companies Act 2013</div>
      <div className="text-xs text-muted-foreground mb-3">As of: {d.as_of} · {d.balanced ? <span className="text-emerald-600">Balanced ✓</span> : <span className="text-red-600">Not balanced</span>} · <span className="italic">Click any line to see contributing accounts.</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <table className="w-full dense">
          <thead><tr><th className="text-left">EQUITY & LIABILITIES</th><th className="text-right">₹</th></tr></thead>
          <tbody>
            {sect("Shareholders' Funds", ["share_capital", "reserves_and_surplus", "total"], d.equity_and_liabilities.shareholders_funds)}
            {sect("Non-Current Liabilities", ["long_term_borrowings", "deferred_tax_liabilities_net", "long_term_provisions", "total"], d.equity_and_liabilities.non_current_liabilities)}
            {sect("Current Liabilities", ["short_term_borrowings", "trade_payables", "other_current_liabilities", "short_term_provisions", "total"], d.equity_and_liabilities.current_liabilities)}
            <tr className="border-t bg-accent/5 font-semibold"><td className="py-2">TOTAL</td><td className="text-right tabular-nums font-mono py-2">{fmtINR(d.equity_and_liabilities.grand_total)}</td></tr>
          </tbody>
        </table>
        <table className="w-full dense">
          <thead><tr><th className="text-left">ASSETS</th><th className="text-right">₹</th></tr></thead>
          <tbody>
            {sect("Non-Current Assets", ["fixed_assets_tangible", "fixed_assets_intangible", "capital_wip", "non_current_investments", "long_term_loans_and_advances", "total"], d.assets.non_current_assets)}
            {sect("Current Assets", ["current_investments", "inventories", "trade_receivables", "cash_and_cash_equivalents", "short_term_loans_and_advances", "other_current_assets", "total"], d.assets.current_assets)}
            <tr className="border-t bg-accent/5 font-semibold"><td className="py-2">TOTAL</td><td className="text-right tabular-nums font-mono py-2">{fmtINR(d.assets.grand_total)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ExportBtn = ({ path, filename, testid }) => (
  <Button size="sm" variant="outline" onClick={() => downloadXlsx(path, filename)} className="rounded-sm gap-1.5" data-testid={testid}>
    <Download className="w-3.5 h-3.5" /> Excel
  </Button>
);

const TB = () => {
  const { from, to } = useContext(FilterCtx);
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/trial-balance", { params: { from_date: from, to_date: to } }).then((r) => setD(r.data)); }, [from, to]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  return (
    <div className="card-sharp overflow-hidden">
      <table className="w-full dense">
        <thead><tr><th>Code</th><th>Account</th><th>Type</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
        <tbody>
          {d.rows.map((r) => (
            <tr
              key={r.account_id || r.code}
              className={r.account_id ? "cursor-pointer group hover:bg-muted/40" : ""}
              onClick={() => r.account_id && drill.openAccount(r.account_id, `${r.code} · ${r.name}`, { from_date: from, to_date: to })}
              data-testid={`tb-row-${r.code}`}
            >
              <td className="font-mono text-xs">{r.code}</td>
              <td>
                <span className={r.account_id ? "group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2" : ""}>{r.name}</span>
                <EntryChip count={r.entry_count} testid={`tb-chip-${r.code}`} />
              </td>
              <td className="text-xs uppercase text-muted-foreground font-mono">{r.account_type}</td>
              <td className="text-right tabular-nums">{r.debit ? fmtINR(r.debit) : "—"}</td>
              <td className="text-right tabular-nums">{r.credit ? fmtINR(r.credit) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-foreground/20"><tr>
          <td colSpan="3" className="text-right pt-2 font-semibold">Total</td>
          <td className="text-right tabular-nums font-mono font-bold pt-2">{fmtINR(d.total_debit)}</td>
          <td className="text-right tabular-nums font-mono font-bold pt-2">{fmtINR(d.total_credit)}</td>
        </tr></tfoot>
      </table>
    </div>
  );
};

// Shared row renderer for BS / P&L flat views — makes each account clickable + shows entry chip.
function DrillRow({ r, onOpen, testid }) {
  const has = !!r.account_id;
  return (
    <tr
      className={`border-b border-border/50 ${has ? "cursor-pointer group hover:bg-muted/40" : ""}`}
      onClick={() => has && onOpen(r)}
      data-testid={testid}
    >
      <td className="py-2 font-mono text-xs text-muted-foreground">{r.code}</td>
      <td className="py-2">
        <span className={has ? "group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2" : ""}>{r.name}</span>
        <EntryChip count={r.entry_count} />
      </td>
      <td className="py-2 text-right tabular-nums">{fmtINR(r.amount)}</td>
    </tr>
  );
}

const PL = () => {
  const { from, to } = useContext(FilterCtx);
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/pnl", { params: { from_date: from, to_date: to } }).then((r) => setD(r.data)); }, [from, to]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const open = (r) => drill.openAccount(r.account_id, `${r.code} · ${r.name}`, { from_date: from, to_date: to });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card-sharp p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 mb-3">Income</div>
        <table className="w-full text-sm">
          <tbody>
            {d.income.map((r) => (
              <DrillRow key={r.account_id || r.code} r={r} onOpen={open} testid={`pl-income-row-${r.code}`} />
            ))}
          </tbody>
          <tfoot><tr className="border-t-2"><td colSpan="2" className="pt-2 font-semibold">Total Income</td><td className="pt-2 text-right tabular-nums font-mono font-bold">{fmtINR(d.total_income)}</td></tr></tfoot>
        </table>
      </div>
      <div className="card-sharp p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Expense</div>
        <table className="w-full text-sm">
          <tbody>
            {d.expense.map((r) => (
              <DrillRow key={r.account_id || r.code} r={r} onOpen={open} testid={`pl-expense-row-${r.code}`} />
            ))}
          </tbody>
          <tfoot><tr className="border-t-2"><td colSpan="2" className="pt-2 font-semibold">Total Expense</td><td className="pt-2 text-right tabular-nums font-mono font-bold">{fmtINR(d.total_expense)}</td></tr></tfoot>
        </table>
      </div>
      <div className="card-sharp p-5 md:col-span-2 flex items-center justify-between bg-primary text-primary-foreground">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Net Profit / (Loss)</div>
          <div className="text-xs opacity-60 mt-1">Income − Expense</div>
        </div>
        <div className={`metric-value text-4xl ${d.net_profit >= 0 ? "" : "text-red-400"}`}>{fmtINR(d.net_profit)}</div>
      </div>
    </div>
  );
};

const BS = () => {
  const { to } = useContext(FilterCtx);
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/balance-sheet", { params: { as_of: to } }).then((r) => setD(r.data)); }, [to]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const open = (r) => drill.openAccount(r.account_id, `${r.code} · ${r.name}`, { to_date: to });
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card-sharp p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Assets</div>
        <table className="w-full text-sm">
          <tbody>{d.assets.map((r) => <DrillRow key={r.account_id || r.code} r={r} onOpen={open} testid={`bs-asset-row-${r.code}`} />)}</tbody>
          <tfoot><tr className="border-t-2"><td colSpan="2" className="pt-2 font-semibold">Total Assets</td><td className="pt-2 text-right tabular-nums font-mono font-bold">{fmtINR(d.total_assets)}</td></tr></tfoot>
        </table>
      </div>
      <div className="card-sharp p-5 space-y-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Liabilities</div>
          <table className="w-full text-sm">
            <tbody>{d.liabilities.map((r) => <DrillRow key={r.account_id || r.code} r={r} onOpen={open} testid={`bs-liab-row-${r.code}`} />)}</tbody>
            <tfoot><tr className="border-t"><td colSpan="2" className="pt-2 font-medium text-xs">Total Liabilities</td><td className="pt-2 text-right tabular-nums font-mono font-semibold">{fmtINR(d.total_liabilities)}</td></tr></tfoot>
          </table>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Equity</div>
          <table className="w-full text-sm">
            <tbody>
              {d.equity.map((r) => <DrillRow key={r.account_id || r.code} r={r} onOpen={open} testid={`bs-equity-row-${r.code}`} />)}
              <tr className="border-b border-border/50"><td className="py-2 font-mono text-xs text-muted-foreground">—</td><td className="py-2">Retained Earnings (YTD)</td><td className="py-2 text-right tabular-nums">{fmtINR(d.retained_earnings)}</td></tr>
            </tbody>
            <tfoot><tr className="border-t"><td colSpan="2" className="pt-2 font-medium text-xs">Total Equity</td><td className="pt-2 text-right tabular-nums font-mono font-semibold">{fmtINR(d.total_equity)}</td></tr></tfoot>
          </table>
        </div>
        <div className="border-t-2 pt-3 flex justify-between font-semibold">
          <span>Total Liab + Equity</span>
          <span className="font-mono tabular-nums">{fmtINR(d.total_liabilities + d.total_equity)}</span>
        </div>
      </div>
    </div>
  );
};

const GST = () => {
  const { from, to } = useContext(FilterCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/gst-summary", { params: { from_date: from, to_date: to } }).then((r) => setD(r.data)); }, [from, to]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card-sharp p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Outward supplies</div>
        <div className="text-xs text-muted-foreground mt-1">Period: {d.period.from} → {d.period.to}</div>
        <table className="w-full text-sm mt-4">
          <tbody>
            <tr className="border-b"><td className="py-2">Taxable value</td><td className="text-right tabular-nums">{fmtINR(d.outward.taxable)}</td></tr>
            <tr className="border-b"><td className="py-2">Output CGST</td><td className="text-right tabular-nums">{fmtINR(d.outward.cgst)}</td></tr>
            <tr className="border-b"><td className="py-2">Output SGST</td><td className="text-right tabular-nums">{fmtINR(d.outward.sgst)}</td></tr>
            <tr className="border-b"><td className="py-2">Output IGST</td><td className="text-right tabular-nums">{fmtINR(d.outward.igst)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="card-sharp p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Inward supplies</div>
        <div className="text-xs text-muted-foreground mt-1">Input tax credit</div>
        <table className="w-full text-sm mt-4">
          <tbody>
            <tr className="border-b"><td className="py-2">Taxable value</td><td className="text-right tabular-nums">{fmtINR(d.inward.taxable)}</td></tr>
            <tr className="border-b"><td className="py-2">Input CGST</td><td className="text-right tabular-nums">{fmtINR(d.inward.cgst)}</td></tr>
            <tr className="border-b"><td className="py-2">Input SGST</td><td className="text-right tabular-nums">{fmtINR(d.inward.sgst)}</td></tr>
            <tr className="border-b"><td className="py-2">Input IGST</td><td className="text-right tabular-nums">{fmtINR(d.inward.igst)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="card-sharp p-5 md:col-span-2 bg-primary text-primary-foreground flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Net GST Payable</div>
          <div className="text-xs opacity-60 mt-1">Output − Input</div>
        </div>
        <div className="metric-value text-4xl">{fmtINR(d.net_gst_payable)}</div>
      </div>
    </div>
  );
};

const Aging = ({ type }) => {
  const { to } = useContext(FilterCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/aging", { params: { party_type: type, as_of: to } }).then((r) => setD(r.data)); }, [type, to]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(d.buckets).map(([k, v]) => (
          <div key={k} className="card-sharp p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k} days</div>
            <div className="metric-value text-2xl mt-2">{fmtINR(v)}</div>
          </div>
        ))}
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Doc #</th><th>Party</th><th>Date</th><th>Bucket</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            {d.rows.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-muted-foreground">No open items</td></tr>}
            {d.rows.map((r) => (
              <tr key={r.doc_no}>
                <td className="font-mono text-xs">{r.doc_no}</td><td>{r.party_name}</td><td className="text-xs">{fmtDate(r.date)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${r.bucket === "0-30" ? "text-emerald-600" : r.bucket === "31-60" ? "text-warning" : "text-red-500"}`}>{r.bucket}</span></td>
                <td className="text-right tabular-nums">{fmtINR(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CashFlow = () => {
  const { from, to } = useContext(FilterCtx);
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/cash-flow", { params: { from_date: from, to_date: to } }).then((r) => setD(r.data)); }, [from, to]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const openCategory = async (c) => {
    try {
      const r = await api.get("/reports/cash-flow/details", { params: { section: c.section, name: c.name, from_date: from, to_date: to } });
      drill.openVouchers(`${c.section.toUpperCase()} · ${c.name} · ${fmtINR(c.amount)}`, r.data.vouchers || [], r.data.total_amount || 0);
    } catch (e) { /* silent */ }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {[["Operating", d.operating], ["Investing", d.investing], ["Financing", d.financing], ["Net Change", d.net_change]].map(([l, v]) => (
          <div key={l} className="card-sharp p-5" data-testid={`cf-summary-${l.toLowerCase().replace(" ", "-")}`}>
            <div className="text-[10px] font-mono uppercase text-muted-foreground">{l}</div>
            <div className={`metric-value text-2xl mt-2 ${v >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(v)}</div>
          </div>
        ))}
      </div>
      <div className="card-sharp overflow-hidden">
        <div className="px-4 py-2 border-b text-xs text-muted-foreground italic">Click any category to see the contributing vouchers.</div>
        <table className="w-full dense">
          <thead><tr><th>Section</th><th>Category</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            {d.categories.length === 0 && <tr><td colSpan="3" className="text-center py-8 text-muted-foreground">No cash movements yet</td></tr>}
            {d.categories.map((c, i) => (
              <tr
                key={i}
                className="cursor-pointer group hover:bg-muted/40"
                onClick={() => openCategory(c)}
                data-testid={`cf-row-${i}`}
              >
                <td className="text-[10px] font-mono uppercase">{c.section}</td>
                <td>
                  <span className="group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2">{c.name}</span>
                </td>
                <td className={`text-right tabular-nums font-mono ${c.amount >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtINR(c.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CashForecast = () => {
  const [d, setD] = useState(null);
  const [weeks, setWeeks] = useState(4);
  useEffect(() => { api.get("/reports/cash-forecast", { params: { weeks } }).then((r) => setD(r.data)); }, [weeks]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="text-[10px] font-mono uppercase text-muted-foreground">Weeks ahead:</label>
        {[1, 2, 4, 8, 13].map((w) => (
          <button key={w} onClick={() => setWeeks(w)} data-testid={`forecast-${w}w`}
                  className={`px-3 py-1 rounded-sm text-xs font-mono ${weeks === w ? "bg-primary text-primary-foreground" : "border hover:bg-muted"}`}>{w}w</button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground">Opening cash: <span className="font-mono">{fmtINR(d.opening_cash)}</span></div>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Week</th><th>From</th><th>To</th><th className="text-right">Inflows</th><th className="text-right">Outflows</th><th className="text-right">Net</th><th className="text-right">Closing Cash</th></tr></thead>
          <tbody>
            {d.weeks.map((w) => (
              <tr key={w.week}>
                <td className="font-mono text-xs">W{w.week}</td>
                <td className="text-xs">{w.from}</td><td className="text-xs">{w.to}</td>
                <td className="text-right tabular-nums text-emerald-600">{fmtINR(w.inflows)}</td>
                <td className="text-right tabular-nums text-red-500">{fmtINR(w.outflows)}</td>
                <td className={`text-right tabular-nums font-medium ${w.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(w.net)}</td>
                <td className="text-right tabular-nums font-mono">{fmtINR(w.closing_cash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Reports() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(today());
  const drill = useDrillDown();
  // Iter-221 · Controlled Tabs that honour the `?tab=` URL param so
  // legacy `/app/financial-statements → /app/reports?tab=fs` and other
  // deep-links actually land on the right tab. Falls back to Trial
  // Balance when the param is absent / unknown.
  const [searchParams, setSearchParams] = useSearchParams();
  const _validTabs = ["tb", "fs", "registers", "gst", "ar", "ap", "cf", "fc",
                       "bomrev", "wfreg", "poviol", "resstock", "asmvar",
                       "valrec", "slowmv", "ageing", "expiry", "recall"];
  const _urlTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(_validTabs.includes(_urlTab) ? _urlTab : "tb");
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && _validTabs.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const onTabChange = (v) => {
    setActiveTab(v);
    // Keep the URL in sync so a back-forward works + legacy links keep
    // their meaning after a page reload.
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.set("tab", v);
      return n;
    }, { replace: true });
  };
  return (
    <FilterCtx.Provider value={{ from, to, setFrom, setTo }}>
      <DrillProvider drill={drill}>
        <div className="p-6 lg:p-8 space-y-6" data-testid="reports-page">
          <PageHeader eyebrow="Insights" title="Financial Reports" description="Click any account or Sch III line to drill down — from summary rows to underlying vouchers, in one interface." />
          <FilterBar />
          {/* Iter-221 · Honour `?tab=fs` etc. so legacy /app/financial-
              statements → /app/reports?tab=fs redirect actually lands on
              the right tab (controlled Tabs). */}
          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList className="rounded-sm flex-wrap h-auto">
              <TabsTrigger value="tb" className="rounded-sm" data-testid="tab-tb">Trial Balance</TabsTrigger>
              <TabsTrigger value="fs" className="rounded-sm" data-testid="tab-fs">Financial Statements</TabsTrigger>
              <TabsTrigger value="registers" className="rounded-sm" data-testid="tab-registers">Registers &amp; Books</TabsTrigger>
              <TabsTrigger value="gst" className="rounded-sm" data-testid="tab-gst">GST Summary</TabsTrigger>
              <TabsTrigger value="ar" className="rounded-sm" data-testid="tab-ar">AR Aging</TabsTrigger>
              <TabsTrigger value="ap" className="rounded-sm" data-testid="tab-ap">AP Aging</TabsTrigger>
              <TabsTrigger value="cf" className="rounded-sm" data-testid="tab-cf">Cash Flow</TabsTrigger>
              <TabsTrigger value="fc" className="rounded-sm" data-testid="tab-fc">Cash Forecast</TabsTrigger>
              <TabsTrigger value="bomrev" className="rounded-sm" data-testid="tab-bom-rev">BOM Rev Audit</TabsTrigger>
              <TabsTrigger value="wfreg" className="rounded-sm" data-testid="tab-workflow-registers">Workflow Registers</TabsTrigger>
              <TabsTrigger value="poviol" className="rounded-sm" data-testid="tab-po-violations">PO Violations</TabsTrigger>
              <TabsTrigger value="resstock" className="rounded-sm" data-testid="tab-reserved-stock">Reserved Stock</TabsTrigger>
              <TabsTrigger value="asmvar" className="rounded-sm" data-testid="tab-assembly-variance">Assembly Variance</TabsTrigger>
              <TabsTrigger value="valrec" className="rounded-sm" data-testid="tab-valuation-recon">Valuation Recon</TabsTrigger>
              <TabsTrigger value="slowmv" className="rounded-sm" data-testid="tab-slow-moving">Slow-Moving Stock</TabsTrigger>
              <TabsTrigger value="ageing" className="rounded-sm" data-testid="tab-stock-ageing">Stock Ageing</TabsTrigger>
              <TabsTrigger value="expiry" className="rounded-sm" data-testid="tab-expiry">Expiry Watchdog</TabsTrigger>
              <TabsTrigger value="recall" className="rounded-sm" data-testid="tab-batch-recall">Batch Recall</TabsTrigger>
            </TabsList>
            <TabsContent value="tb" className="mt-4 space-y-3"><div className="flex justify-end gap-2"><SendReportButton reportLabel="Trial Balance" /><ExportBtn path={`/reports/trial-balance/export?from_date=${from}&to_date=${to}`} filename="trial-balance.xlsx" testid="export-tb" /></div><TB /></TabsContent>
            <TabsContent value="fs" className="mt-4"><ConstitutionAwareFS asOf={to} fromDate={from} toDate={to} /></TabsContent>
            <TabsContent value="registers" className="mt-4"><Registers /></TabsContent>
            <TabsContent value="gst" className="mt-4 space-y-3"><div className="flex justify-end gap-2"><SendReportButton reportLabel="GST Summary" /><ExportBtn path={`/reports/gst-summary/export?from_date=${from}&to_date=${to}`} filename="gst-summary.xlsx" testid="export-gst" /></div><GST /></TabsContent>
            <TabsContent value="ar" className="mt-4 space-y-3"><div className="flex justify-end gap-2"><SendReportButton reportLabel="AR Aging" /><ExportBtn path={`/reports/aging/export?party_type=customer&as_of=${to}`} filename="ar-aging.xlsx" testid="export-ar" /></div><Aging type="customer" /></TabsContent>
            <TabsContent value="ap" className="mt-4 space-y-3"><div className="flex justify-end gap-2"><SendReportButton reportLabel="AP Aging" /><ExportBtn path={`/reports/aging/export?party_type=vendor&as_of=${to}`} filename="ap-aging.xlsx" testid="export-ap" /></div><Aging type="vendor" /></TabsContent>
            <TabsContent value="cf" className="mt-4"><CashFlow /></TabsContent>
            <TabsContent value="fc" className="mt-4"><CashForecast /></TabsContent>
            <TabsContent value="bomrev" className="mt-4"><BOMRevAuditor /></TabsContent>
            <TabsContent value="wfreg" className="mt-4"><WorkflowRegisters /></TabsContent>
            <TabsContent value="poviol" className="mt-4"><POViolations /></TabsContent>
            <TabsContent value="resstock" className="mt-4"><ReservedStockLedger /></TabsContent>
            <TabsContent value="asmvar" className="mt-4"><AssemblyVariance /></TabsContent>
            <TabsContent value="valrec" className="mt-4"><ValuationRecon /></TabsContent>
            <TabsContent value="slowmv" className="mt-4"><SlowMovingStock /></TabsContent>
            <TabsContent value="ageing" className="mt-4"><StockAgeing /></TabsContent>
            <TabsContent value="expiry" className="mt-4"><ExpiryWatchdog /></TabsContent>
            <TabsContent value="recall" className="mt-4"><BatchRecall /></TabsContent>
          </Tabs>
        </div>
      </DrillProvider>
    </FilterCtx.Provider>
  );
}
